import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { Ellipsis, KeyRound, LayoutGrid, LogOut, MonitorSmartphone, ShieldAlert, ShieldCheck, UserRound, BellRing, LifeBuoy, Scale } from 'lucide-react';
import { OrgSwitcher } from '@features/auth/components/OrgSwitcher';
import { useChangeLocale } from '@features/auth/model/locale';
import { dashboardContext, signOut } from '@features/auth/model/session';
import { cn } from '@shared/lib/cn';
import { DASHBOARD_PATHS, DASHBOARDS, dashboardsFor, type Dashboard } from '@shared/lib/roles';
import { useSession } from '@shared/session/store';
import {
  Avatar,
  Banner,
  Drawer,
  Icon,
  ImpersonationBanner,
  LanguageSwitcher,
  Logo,
  LogoMark,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
  ThemeToggle,
} from '@shared/ui';
import { isActivePath, phoneNav, visibleNav, type NavItem } from '../navigation';
import { NotificationBell } from '@features/notifications/components/NotificationBell';
import { TodayHijri } from '@features/calendar/components/Hijri';

function dashboardFromPath(pathname: string): Dashboard | null {
  const raw = pathname.split('/')[2];
  // The super-admin console lives under /app/platform/*.
  const segment = raw === 'platform' ? 'super-admin' : raw;
  return (DASHBOARDS as readonly string[]).includes(segment ?? '') ? (segment as Dashboard) : null;
}

let lastDashboard: Dashboard | null = null;

function useNavState() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const me = useSession((s) => s.me);
  const user = useSession((s) => s.user);
  const memberships = useSession((s) => s.memberships);
  const activeOrganizationId = useSession((s) => s.activeOrganizationId);
  return useMemo(() => {
    const ctx = dashboardContext({ me, user, memberships, activeOrganizationId });
    const available = dashboardsFor(ctx);
    const fromPath = dashboardFromPath(pathname);
    if (fromPath && available.includes(fromPath)) lastDashboard = fromPath;
    const current: Dashboard | null =
      fromPath && available.includes(fromPath) ? fromPath : lastDashboard && available.includes(lastDashboard) ? lastDashboard : (available[0] ?? null);
    const items = current ? visibleNav(current, { roles: ctx.roles, soloMode: Boolean(me?.solo_mode) }) : [];
    return { pathname, available, current, items };
  }, [pathname, me, user, memberships, activeOrganizationId]);
}

function NavLinkItem({
  item,
  pathname,
  compact,
  onNavigate,
}: {
  item: NavItem;
  pathname: string;
  compact?: boolean;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation('nav');
  const active = isActivePath(item, pathname);
  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'group flex min-h-touch-min items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors duration-micro md:min-h-control-md',
        active ? 'bg-primary-subtle text-on-primary-subtle' : 'text-secondary hover:bg-surface-tint hover:text-primary',
        compact && 'max-lg:justify-center max-lg:px-0',
      )}
    >
      <Icon icon={item.icon} size={20} className={active ? 'text-brand' : undefined} />
      <span className={cn(compact && 'max-lg:sr-only')}>{t(item.label as 'student.home')}</span>
    </Link>
  );
}

function Sidebar({ items, pathname, available, current }: ReturnType<typeof useNavState>) {
  const { t } = useTranslation('nav');
  return (
    <aside
      aria-label={t('sidebar.label')}
      className="sticky top-0 hidden h-dvh w-sidebar-collapsed shrink-0 flex-col border-e border-border-subtle bg-surface md:flex lg:w-sidebar"
    >
      <div className="flex h-topbar items-center px-4 max-lg:justify-center">
        <Link to={current ? DASHBOARD_PATHS[current] : '/app'} aria-label={t('home')} className="rounded-md">
          <span className="lg:hidden">
            <LogoMark size={32} />
          </span>
          <span className="hidden lg:inline-flex">
            <Logo size={32} />
          </span>
        </Link>
      </div>
      <nav aria-label={t('main.label')} className="flex-1 overflow-y-auto px-3 py-2">
        <ul className="flex flex-col gap-1">
          {items.map((item) => (
            <li key={item.id}>
              {item.section ? (
                <p className="mb-1 mt-4 px-3 text-xs font-semibold text-muted max-lg:sr-only">
                  {t(item.section as 'admin.sectionPeople')}
                </p>
              ) : null}
              <NavLinkItem item={item} pathname={pathname} compact />
            </li>
          ))}
        </ul>
      </nav>
      {available.length > 1 ? (
        <div className="border-t border-border-subtle p-3">
          <p className="mb-1 px-3 text-xs font-medium text-muted max-lg:sr-only">{t('dashboards.label')}</p>
          <ul className="flex flex-col gap-1">
            {available.map((d) => (
              <li key={d}>
                <Link
                  to={DASHBOARD_PATHS[d]}
                  aria-current={d === current ? 'true' : undefined}
                  className={cn(
                    'flex min-h-control-sm items-center gap-2 rounded-md px-3 text-sm',
                    d === current ? 'font-medium text-brand' : 'text-secondary hover:bg-surface-tint',
                    'max-lg:justify-center max-lg:px-0',
                  )}
                >
                  <Icon icon={LayoutGrid} size={16} />
                  <span className="max-lg:sr-only">{t(`dashboards.${d}` as 'dashboards.student')}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </aside>
  );
}

function UserMenu({ available, current }: { available: Dashboard[]; current: Dashboard | null }) {
  const { t } = useTranslation(['nav', 'common', 'legal']);
  const navigate = useNavigate();
  const user = useSession((s) => s.user);
  const name = user?.full_name ?? '';
  return (
    <Menu>
      <MenuTrigger asChild>
        <button
          type="button"
          aria-label={t('nav:account.menu')}
          className="inline-flex min-h-touch-min items-center gap-2 rounded-full p-1 hover:bg-surface-tint md:min-h-0"
        >
          <Avatar name={name} size="sm" />
        </button>
      </MenuTrigger>
      <MenuContent label={t('nav:account.menu')}>
        <MenuLabel>
          <bdi className="block truncate text-sm font-medium text-primary">{name}</bdi>
          <bdi className="ltr-island block truncate">{user?.email}</bdi>
        </MenuLabel>
        <MenuSeparator />
        <MenuItem icon={UserRound} onSelect={() => void navigate({ to: '/app/account/profile' })}>
          {t('nav:account.profile')}
        </MenuItem>
        <MenuItem icon={ShieldCheck} onSelect={() => void navigate({ to: '/app/account/security' })}>
          {t('nav:account.security')}
        </MenuItem>
        <MenuItem icon={MonitorSmartphone} onSelect={() => void navigate({ to: '/app/account/sessions' })}>
          {t('nav:account.sessions')}
        </MenuItem>
        <MenuItem icon={BellRing} onSelect={() => void navigate({ to: '/app/account/notifications' })}>
          {t('nav:account.notifications')}
        </MenuItem>
        <MenuItem icon={LifeBuoy} onSelect={() => void navigate({ to: '/app/help' })}>
          {t('nav:account.help')}
        </MenuItem>
        <MenuItem icon={Scale} onSelect={() => void navigate({ to: '/legal/terms' })}>
          {t('legal:menu.policies')}
        </MenuItem>
        {available.length > 1 ? (
          <>
            <MenuSeparator />
            <MenuLabel>{t('nav:dashboards.label')}</MenuLabel>
            {available.map((d) => (
              <MenuItem key={d} icon={LayoutGrid} onSelect={() => void navigate({ to: DASHBOARD_PATHS[d] })}>
                {t(`nav:dashboards.${d}` as 'nav:dashboards.student')}
                {d === current ? <span className="sr-only"> {t('nav:dashboards.current')}</span> : null}
              </MenuItem>
            ))}
          </>
        ) : null}
        <MenuSeparator />
        <MenuItem
          icon={LogOut}
          destructive
          onSelect={async () => {
            await signOut();
            await navigate({ to: '/login' });
          }}
        >
          {t('nav:account.signOut')}
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}

function BottomNav({ items, pathname }: { items: readonly NavItem[]; pathname: string }) {
  const { t } = useTranslation('nav');
  const [moreOpen, setMoreOpen] = useState(false);
  const { primary, overflow } = phoneNav(items);
  const overflowActive = overflow.some((i) => isActivePath(i, pathname));
  if (items.length === 0) return null;
  return (
    <>
      <nav
        aria-label={t('bottom.label')}
        className="fixed inset-x-0 bottom-0 z-sticky border-t border-border-subtle bg-surface pb-[env(safe-area-inset-bottom)] md:hidden"
      >
        <ul className="flex h-bottomnav items-stretch">
          {primary.map((item) => {
            const active = isActivePath(item, pathname);
            return (
              <li key={item.id} className="flex-1">
                <Link
                  to={item.to}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'flex h-full flex-col items-center justify-center gap-0.5 text-xs',
                    active ? 'font-semibold text-brand' : 'text-muted',
                  )}
                >
                  <Icon icon={item.icon} size={22} strokeWidth={active ? 2 : 1.5} />
                  <span className="max-w-full truncate px-1">{t(item.label as 'student.home')}</span>
                </Link>
              </li>
            );
          })}
          {overflow.length > 0 ? (
            <li className="flex-1">
              <button
                type="button"
                onClick={() => setMoreOpen(true)}
                aria-haspopup="dialog"
                className={cn(
                  'flex h-full w-full flex-col items-center justify-center gap-0.5 text-xs',
                  overflowActive ? 'font-semibold text-brand' : 'text-muted',
                )}
              >
                <Icon icon={Ellipsis} size={22} />
                <span>{t('bottom.more')}</span>
              </button>
            </li>
          ) : null}
        </ul>
      </nav>
      <Drawer open={moreOpen} onOpenChange={setMoreOpen} title={t('bottom.more')}>
        <ul className="flex flex-col gap-1">
          {overflow.map((item) => (
            <li key={item.id}>
              <NavLinkItem item={item} pathname={pathname} onNavigate={() => setMoreOpen(false)} />
            </li>
          ))}
        </ul>
      </Drawer>
    </>
  );
}

function MfaBanner() {
  const { t } = useTranslation('auth');
  const required = useSession((s) => s.mfaEnrollmentRequired);
  const enabled = useSession((s) => Boolean(s.user?.mfa_enabled));
  if (!required || enabled) return null;
  return (
    <Banner
      tone="warning"
      icon={ShieldAlert}
      action={
        <Link to="/app/account/security" className="link inline-flex items-center gap-1 font-medium">
          <Icon icon={KeyRound} size={16} />
          {t('mfa.bannerAction')}
        </Link>
      }
    >
      {t('mfa.bannerBody')}
    </Banner>
  );
}

function ImpersonationNotice() {
  const impersonation = useSession((s) => s.impersonation);
  const setImpersonation = useSession((s) => s.setImpersonation);
  if (!impersonation) return null;
  return (
    <ImpersonationBanner
      subjectName={impersonation.subjectName}
      actorName={impersonation.actorName}
      // POST /auth/impersonation/stop lands with the super-admin module (API spec §2.1).
      onExit={() => setImpersonation(null)}
    />
  );
}

/** Responsive shell: sidebar (rail at md, full at lg) + top bar on desktop; bottom nav on phone. */
export function AppShell() {
  const { t } = useTranslation('nav');
  const nav = useNavState();
  const changeLocale = useChangeLocale();
  return (
    <div className="flex min-h-dvh bg-bg">
      <Sidebar {...nav} />
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-sticky border-b border-border-subtle bg-surface">
          <div className="flex h-topbar items-center gap-2 px-page-x lg:px-8">
            <Link to={nav.current ? DASHBOARD_PATHS[nav.current] : '/app'} aria-label={t('home')} className="rounded-md md:hidden">
              <LogoMark size={28} />
            </Link>
            <OrgSwitcher />
            <TodayHijri />
            <div className="ms-auto flex items-center gap-1">
              <NotificationBell />
              <LanguageSwitcher onChange={(l) => void changeLocale(l)} />
              <ThemeToggle />
              <UserMenu available={nav.available} current={nav.current} />
            </div>
          </div>
          <ImpersonationNotice />
          <MfaBanner />
        </header>
        <main id="main" tabIndex={-1} className="flex-1 px-page-x pb-[calc(var(--size-bottomnav)+2rem)] pt-6 outline-none md:pb-12 lg:px-8">
          <div className="mx-auto w-full max-w-content">
            <Outlet />
          </div>
        </main>
      </div>
      <BottomNav items={nav.items} pathname={nav.pathname} />
    </div>
  );
}
